import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Label, Row } from 'reactstrap';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import { config } from 'src/config';
import { setFromErrors } from 'src/helpers';
import { useCreateThesisExaminerMutation } from 'src/rtk/features/disertation-thesis/thesisMarksheetApi';
import { useLazyGetDepartmentsByInstituteQuery } from 'src/rtk/features/setup/department/departmentApi';
import { useGetOrganizationsQuery } from 'src/rtk/features/setup/organization/setupOrganizationApi';
import { useLazyGetSupervisorsByDepartmentQuery } from 'src/rtk/features/trainingRecord/trainingRecordlApi';
import * as Yup from 'yup';
import loader from '../../assets/images/loader.gif';

type SelectOption = { value: string | number; label: string };

const MakeExaminerPanel: React.FC = () => {
    const history = useHistory();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
    const [supervisors, setSupervisors] = useState<any[]>([]);

    const [addedIds, setAddedIds] = useState<string[]>([]);
    const [existingAssigned, setExistingAssigned] = useState<{ examiner_id: string; deletable_id?: string }[]>([]); // assignments coming from API
    const [removedExisting, setRemovedExisting] = useState<{ examiner_id: string; deletable_id: string }[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const { data: instituteLookupData } = useGetOrganizationsQuery();
    const allInstitute: SelectOption[] =
        instituteLookupData?.data?.map((institute: any) => ({
            value: institute.id!,
            label: institute.organization_name,
        })) || [];

    const [getDepartmentsByInstitute, { data: departmentLookupData, isLoading: isDepartmentLoading, isFetching: isDepartmentFetching }] = useLazyGetDepartmentsByInstituteQuery();
    const [getSupervisorsByDepartment, { data: supervisorData, isLoading: isSupervisorLoading, isFetching: isSupervisorFetching }] = useLazyGetSupervisorsByDepartmentQuery();
    const [createThesisExaminer, { isSuccess, isError, error, isLoading }] = useCreateThesisExaminerMutation();

    const err: CustomError = error as CustomError;

    const allDepartment: SelectOption[] =
        departmentLookupData?.data?.map((dept: any) => ({
            value: dept.id!,
            label: dept.department_name || dept.name || `Dept ${dept.id}`,
        })) || [];


    useEffect(() => {
        setSelectedDepartment(null);
        setSupervisors([]);
        setSelectAll(false);
        if (selectedInstitute) {
            getDepartmentsByInstitute(String(selectedInstitute.label));
        }
    }, [selectedInstitute, getDepartmentsByInstitute]);

    useEffect(() => {
        setSupervisors([]);
        setSelectAll(false);
        if (selectedDepartment) {
            const instituteId = selectedInstitute ? String(selectedInstitute.value) : '';
            getSupervisorsByDepartment({
                institute_id: instituteId,
                department_name: String(selectedDepartment.label),
            });
        }
    }, [selectedDepartment, selectedInstitute, getSupervisorsByDepartment]);

    useEffect(() => {
        if (!supervisorData?.data) return;

        setSupervisors(supervisorData.data);

        const perSupervisorAssigned = supervisorData.data
            .map((s: any) => {
                const isExisting = Boolean(s.is_thesis_examiner);
                if (!isExisting) return null;

                const examinerId = String(s.user_id);
                const deletableId = s.thesis_examiner_id;
                return { examiner_id: examinerId, deletable_id: deletableId };
            })
            .filter(Boolean);

        setExistingAssigned(perSupervisorAssigned as any);
    }, [supervisorData]);

    const filteredSupervisors = useMemo(() => {
        if (!searchTerm) return supervisors;
        const q = searchTerm.trim().toLowerCase();
        return supervisors.filter((s: any) => (s.full_name || '').toLowerCase().includes(q));
    }, [searchTerm, supervisors]);

    useEffect(() => {
        const visibleIds = filteredSupervisors.map((s: any) => String(s.user_id)).filter(Boolean);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => {
            if (addedIds.includes(id)) return true;
            const wasExisting = existingAssigned.find(e => e.examiner_id === id);
            if (wasExisting) {
                return !removedExisting.find(r => r.examiner_id === id);
            }
            return false;
        });
        setSelectAll(allSelected);
    }, [filteredSupervisors, addedIds, existingAssigned, removedExisting]);

    const isChecked = (s: any) => {
        const id = String(s.user_id);
        if (!id) return false;
        if (addedIds.includes(id)) return true;
        const existing = existingAssigned.find(e => e.examiner_id === id);
        if (existing) {
            return !removedExisting.find(r => r.examiner_id === id);
        }
        return false;
    };

    const toggleSelect = (id: string) => {
        const existing = existingAssigned.find(e => e.examiner_id === id);
        if (existing) {
            const alreadyRemoved = removedExisting.find(r => r.examiner_id === id);
            if (alreadyRemoved) {
                setRemovedExisting(prev => prev.filter(r => r.examiner_id !== id));
            } else {
                if (existing.deletable_id) {
                    setRemovedExisting(prev => [...prev, { examiner_id: id, deletable_id: existing.deletable_id! }]);
                } else {
                    setRemovedExisting(prev => [...prev, { examiner_id: id, deletable_id: '' }]);
                }
            }
            return;
        }

        setAddedIds(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            return [...prev, id];
        });
    };

    const handleSelectAll = (checked: boolean) => {
        const visibleIds = filteredSupervisors.map((s: any) => String(s.user_id)).filter(Boolean);

        setSelectAll(checked);
        if (checked) {
            setAddedIds(prev => {
                const toAdd = visibleIds.filter(id => {
                    if (prev.includes(id)) return false;
                    if (existingAssigned.find(e => e.examiner_id === id)) {
                        setRemovedExisting(prevRem => prevRem.filter(r => r.examiner_id !== id));
                        return false;
                    }
                    return true;
                });
                return Array.from(new Set([...prev, ...toAdd]));
            });
        } else {
            setAddedIds(prev => prev.filter(id => !visibleIds.includes(id)));
            const toRemoveExisting = existingAssigned
                .filter(e => visibleIds.includes(e.examiner_id))
                .map(e => ({ examiner_id: e.examiner_id, deletable_id: e.deletable_id ?? '' }));
            setRemovedExisting(prev => {
                const merged = [...prev];
                toRemoveExisting.forEach(r => {
                    if (!merged.find(m => m.examiner_id === r.examiner_id)) merged.push(r);
                });
                return merged;
            });
        }
    };

    const formik = useFormik({
        initialValues: {
            institute_id: selectedInstitute?.value ?? '',
            department_id: selectedDepartment?.value ?? '',
            examiners: [],
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            institute_id: Yup.mixed().required('Institute is required'),
            department_id: Yup.mixed().required('Department is required'),
            examiners: Yup.mixed().test(
                'has-changes',
                'No changes to send.',
                () => {
                    return addedIds.length + removedExisting.length > 0;
                }
            ),
        }),
        onSubmit: async () => {
            // Build payload array following required backend format
            const payloadArr: any[] = [];
            addedIds.forEach(id => payloadArr.push({ examiner_id: Number(id), add: 1 }));
            removedExisting.forEach(r => payloadArr.push({ examiner_id: Number(r.examiner_id), deletable_id: r.deletable_id }));

            if (payloadArr.length === 0) {
                toast.error('No changes to send.');
                return;
            }

            const body = {
                institute_id: formik.values.institute_id,
                department_id: formik.values.department_id,
                examiners: payloadArr,
            };
            createThesisExaminer(body);
        },
    });

    useEffect(() => {
        formik.setFieldValue('institute_id', selectedInstitute?.value ?? '');
    }, [selectedInstitute]);

    useEffect(() => {
        formik.setFieldValue('department_id', selectedDepartment?.value ?? '');
    }, [selectedDepartment]);

    useEffect(() => {
        if (isSuccess) {
            formik.resetForm();
            toast.success('Examiner Panel Created Successfully!');
            history.push('/rtmd-assign-examiner-for-thesis')
        }
        if (isError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err.data, formik.setErrors);
        }
    }, [isSuccess, isError]);

    return (
        <div className='page-content'>
            {(isLoading || isSupervisorFetching || isDepartmentFetching || isSupervisorLoading || isDepartmentLoading) ? (
                <div className="overlay">
                    <img src={loader} alt="Loading" height={100} width={300} />
                </div>
            ) : null}
            <Container fluid>
                <BetterBreadcrumb
                    title="Examiner Panel"
                    breadcrumbItem={[
                        { link: '#', name: 'Thesis and Dissertation' },
                    ]}
                />
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                    }}
                    onReset={(e) => {
                        formik.resetForm();
                    }}
                >
                    <Card className="mb-4">
                        <CardBody>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="institute">Institute</Label>
                                        <Select
                                            inputId="institute"
                                            options={allInstitute}
                                            value={selectedInstitute}
                                            onChange={(opt: SelectOption) => setSelectedInstitute(opt as SelectOption)}
                                            placeholder="Select institute..."
                                            isClearable
                                            styles={{ menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }) }}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="department">Department</Label>
                                        <Select
                                            inputId="department"
                                            options={allDepartment}
                                            value={selectedDepartment}
                                            onChange={(opt: SelectOption) => setSelectedDepartment(opt as SelectOption)}
                                            placeholder="Select department..."
                                            isClearable
                                            isDisabled={!selectedInstitute}
                                        />
                                    </FormGroup>
                                </Col>

                                {/* <Col md={4}>
                                    <FormGroup>
                                        <Label for="search">Search Supervisor</Label>
                                        <Input
                                            id="search"
                                            type="text"
                                            placeholder="Search by name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col> */}
                            </Row>

                            <Row className="mt-3">
                                <Col>
                                    <div className="d-flex align-items-center mb-2">
                                        <input
                                            id="select-all"
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            className='large-checkbox'
                                        />
                                        <label htmlFor="select-all" className="ms-2 mb-0">
                                            Select All
                                        </label>
                                        <div className="flex-grow-1 text-center fw-bold">
                                            {`Total Selected - (${addedIds.length + existingAssigned.filter(e => !removedExisting.find(r => r.examiner_id === e.examiner_id)).length})`}
                                        </div>
                                        <div className="ms-auto">
                                            <Button color="primary" type="submit" size="md" className='px-4 fw-bold' disabled={(addedIds.length === 0 && removedExisting.length === 0)}>
                                                Save
                                            </Button>
                                        </div>
                                    </div>

                                    <Row>
                                        {filteredSupervisors && filteredSupervisors.length > 0 ? (
                                            filteredSupervisors.map((s: any) => {
                                                const id = String(s.user_id);
                                                const checked = isChecked(s);
                                                return (
                                                    <Col key={id || s.id} lg={3} md={4} sm={6} className="mb-3">
                                                        <Card className="supervisor-card h-100">
                                                            <CardBody className="supervisor-card-body">
                                                                <div className="img-col">
                                                                    <img
                                                                        src={`${config?.APP_URL}${s.picture}`}
                                                                        alt={s.full_name}
                                                                        className="supervisor-img"
                                                                    />
                                                                </div>

                                                                <div className="info-col">
                                                                    <div className="supervisor-name">{s.full_name}</div>
                                                                    <div className="supervisor-reg">BMDC : {s.bmdc}</div>
                                                                </div>

                                                                <div className="chk-col">
                                                                    <input
                                                                        id={`chk-${id}`}
                                                                        type="checkbox"
                                                                        className="large-checkbox"
                                                                        checked={checked}
                                                                        onChange={() => toggleSelect(id)}
                                                                    />
                                                                </div>
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                );
                                            })
                                        ) : (
                                            <Col>
                                                <div className="text-muted">No supervisors found. Select institute & department to load supervisors.</div>
                                            </Col>
                                        )}
                                    </Row>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Form>
            </Container>
        </div>
    );
};

export default MakeExaminerPanel;